import React, { useEffect, useState } from 'react';
// import theme from './HistoryLog.module.scss';
import { useAsyncStateWithIsLoading } from '@/utils/hooks';
import { getHistoryLog, HistorySearchFilters } from '@/services/object-history-log';
import { LoadingIndicator, createDataTable, SortDirection } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { UserInfoRowLazy } from '@/components/UserInfoRow';
import { DateTimeDisplay } from '@/components/localization';
// import { createDataTable } from '@/components/DataTable';
import { ObjectHistoryLog } from '@/types';
import { CustomLink } from '@/components/navigation';
import { ObjectHistoryLogObjectType } from './HistoryLogObjectType';
import { ResidentialUnitDetailsTab } from '../residential-units/ResidentialUnitDetails/ResidentialUnitDetails';
import HistoryLogTableInfoRow from './HistoryLogTableInfoRow';
import { Tooltip } from '@/components/Tooltip';
import { isEqual } from 'lodash-es';
import { useLingui } from '@lingui/react';
export interface HistoryLogProps {
    filter: HistorySearchFilters;
    /**
     * Use to remove columns not needed, usually the "objectType" and "objectName"
     */
    hiddenColumns?: (keyof ObjectHistoryLog)[];
}

const DataTable = createDataTable<ObjectHistoryLog>();

const HistoryLogTable = ({ filter, hiddenColumns }: HistoryLogProps) => {
    const { _ } = useLingui();

    const [fixedFilters, setFixedFilters] = useState(filter);
    useEffect(() => {
        setFixedFilters((prev) => {
            return isEqual(prev, filter) ? prev : filter;
        });
    }, [filter, setFixedFilters]);

    const [tableState, setTableState] = useState(DataTable.getInitialState('id', SortDirection.Descending));
    const [data, , , isLoading] = useAsyncStateWithIsLoading(
        () => getHistoryLog({ ...tableState, ...fixedFilters }),
        [tableState, fixedFilters],
        null
    );

    const getLinkElement = (row: ObjectHistoryLog) => {
        let rowName = row.objectName;

        // For bulk operations, show the bulk display name
        if (
            (row.objectType === 'ProjectDocument' || row.objectType === 'ResidentialUnitDocument') &&
            row.parametersJson &&
            row.parametersJson['isBulkOperation'] &&
            row.parametersJson['bulkDisplayName']
        ) {
            rowName = row.parametersJson['bulkDisplayName'];
        }

        if (row.objectType === 'Email') {
            return rowName;
        }
        let linkObjectId: any = row.objectId;
        let link = '';
        if (row.objectType === 'ProjectDocument' || row.objectType === 'ResidentialUnitDocument') {
            linkObjectId = row.groupById;
        }
        if (row.objectType === 'ProjectCatalogLinkGroup') {
            link = `/projects/${row.projectId}/options/options`; // TODO: Change this when removing options v1
        }
        if (row.objectType === 'Project') {
            link = `/projects/${row.objectId}`;
        }
        if (row.objectType === 'Dependency') {
            link = `/projects/${row.projectId}/options/dependencies/${row.objectId}`; // TODO: Change this when removing options v1
        }
        if (row.objectType === 'OptionPreset' || row.objectType === 'OptionPresetGroup') {
            link = `/projects/${row.projectId}/options/styles/Main`; // TODO: Change this when removing options v1
        } else if (row.residentialUnitId) {
            const basePath = `/projects/${row.projectId}/residential-units/${row.residentialUnitId}`;
            link = `${basePath}/${getResidentialUnitTabPath(row.objectType)}/${linkObjectId}`;
        }
        return <CustomLink to={link}>{rowName}</CustomLink>;
    };

    const getResidentialUnitTabPath = (objectType: ObjectHistoryLogObjectType): ResidentialUnitDetailsTab | null => {
        switch (objectType) {
            case 'ProjectDocument':
            case 'ResidentialUnitDocument':
                return 'documents';
            case 'ChangeRequest':
                return 'changes';
            case 'ExternalAgreement':
                return 'external-agreements';
            case 'ResidentialUnit':
                return 'dashboard';
            case 'Order':
                return 'orders';
            default:
                return null;
        }
    };

    if (!data) {
        return <LoadingIndicator />;
    }

    return (
        <DataTable
            hiddenColumns={hiddenColumns}
            isLoading={isLoading}
            data={data.items}
            onStateChange={setTableState}
            state={tableState}
            totalItemCount={data.totalCount}
            className="table style-3">
            <DataTable.Column
                header={<Trans>Object</Trans>}
                field="objectType"
                sortable
                renderer={(x) => (
                    <Tooltip
                        includeQuestionMark
                        content={
                            x.row.relatedObjectType ? (
                                <>
                                    <Trans>Related to:</Trans>{' '}
                                    {_({ id: `HistoryLog.ObjectType.${x.row.relatedObjectType}` })}
                                </>
                            ) : null
                        }>
                        <span>{_({ id: `HistoryLog.ObjectType.${x.value}` })}</span>
                    </Tooltip>
                )}
            />
            <DataTable.Column
                header={<Trans>Action</Trans>}
                field="operationKey"
                sortable
                renderer={(x) => {
                    // Check if this is a bulk operation
                    if (
                        x.row.parametersJson &&
                        x.row.parametersJson['isBulkOperation'] &&
                        x.row.parametersJson['bulkOperationName']
                    ) {
                        return <span>{x.row.parametersJson['bulkOperationName']}</span>;
                    }
                    return _({ id: `HistoryLog.Action.${x.value}` });
                }}
            />
            <DataTable.Column
                header={<Trans>Name</Trans>}
                field="objectName"
                sortable
                renderer={(x) => getLinkElement(x.row)}
            />
            <DataTable.Column
                header={<Trans>Info</Trans>}
                field="parametersJson"
                renderer={(x) => <HistoryLogTableInfoRow row={x.row} />}
            />
            <DataTable.Column
                header={<Trans>Performed by</Trans>}
                field="performedBy"
                renderer={(x) => (x.value ? <UserInfoRowLazy userId={x.value.id} /> : null)}
            />
            <DataTable.Column
                header={<Trans>Date</Trans>}
                field="performedDate"
                sortable
                renderer={(x) => <DateTimeDisplay value={x.value} />}
            />
        </DataTable>
    );
};

export default HistoryLogTable;
